import { FeatureFlag } from 'types/features';

// ==============================|| USER TYPES  ||============================== //

/** The portal design a user chose. Unset means {@link DEFAULT_UI_DESIGN}. */
export type UiDesign = 'CLASSIC' | 'NEW';

/** The design of everyone who never chose one; changing it moves only those users. */
export const DEFAULT_UI_DESIGN: UiDesign = 'CLASSIC';

/** The new design's colours a user chose. Unset means `SYSTEM`, following the device; classic ignores it. */
export type UiTheme = 'LIGHT' | 'SYSTEM' | 'DARK';

/**
 * Whether the tenant offers the new design. Without it everyone gets classic whatever they stored, and the
 * stored choice is kept, so turning the flag back on gives each user theirs again.
 */
export const hasNewDesign = (user: Pick<UserData, 'enabledFeatures'> | undefined) =>
    !!user?.enabledFeatures?.includes(FeatureFlag.NEW_DESIGN);

/** Whether the user sees the new design: the tenant offers it, and it is their choice, or the default when unset. */
export const isNewDesignOf = (user: Pick<UserData, 'enabledFeatures' | 'preferences'> | undefined) =>
    hasNewDesign(user) && (user?.preferences?.uiDesign ?? DEFAULT_UI_DESIGN) === 'NEW';

interface UserPreferences {
    /** The classic design's choice. The backend reads an unset one as on. */
    highlightRequiredFields: boolean;
    /** Absent until the user chooses: the backend leaves an unset key out of the document. */
    uiDesign?: UiDesign | null;
    newDesignWelcomeSeen?: boolean | null;
    /** The new design's choice. It has no default: absent means off. */
    newDesignHighlightRequiredFields?: boolean | null;
    newDesignTheme?: UiTheme | null;
}

export interface UpdateUserPreferences {
    highlightRequiredFields: boolean;
    uiDesign?: UiDesign | null;
    newDesignWelcomeSeen?: boolean | null;
    newDesignHighlightRequiredFields?: boolean | null;
    newDesignTheme?: UiTheme | null;
}

export interface UserData {
    id: number;
    active: boolean;
    firstName: string;
    lastName: string;
    notes: string | null;
    tenants: string[];
    tenantName: string | null;
    workEmailAddress: string | null;
    permissions: { code: string }[] | null;
    features: Map<string, boolean> | null;
    enabledFeatures: string[] | null;
    /** The back-office settings on for this colleague, and their ticked sub-options as `KEY:option`. */
    enabledSettings: string[] | null;
    /** The fields this colleague must fill to save a record: `FORM:FIELD`, or `FORM:FIELD:KIND` on the Client form. */
    requiredFields: string[] | null;
    jobPositionName: string | null;
    teamName: string | null;
    authUserId: number;
    authUserFirstName: string;
    authUserLastName: string;
    preferences: UserPreferences;
}

/**
 * The tenant's two-factor mode at password sign-in. OFF closes enrolment, OPTIONAL lets users enrol from
 * Profile › Security, and REQUIRED sends them into enrolment at sign-in. Microsoft SSO sign-ins skip it.
 */
export type MfaMode = 'OFF' | 'OPTIONAL' | 'REQUIRED';

/**
 * The signed-in user's own authenticator state. `enrolledAt` is when the portal first saw the
 * enrolment and stays null until a sign-in recorded it.
 */
export interface MfaStatus {
    enrolled: boolean;
    enrolledAt: string | null;
    /** The tenant's two-factor mode; OPTIONAL is what lets a user set an authenticator up from the profile. */
    mode: MfaMode;
    /** A passwordless tenant's customer user: Microsoft sign-in applies its own verification. */
    ssoManaged: boolean;
}
